/** Page navigation for a long list.
 *
 *  Shows the pages either side of where you are, plus the first and last, with
 *  a gap in between. A list of 720 pages cannot show 720 buttons, but it can
 *  always show the two that matter — where you are, and where the ends are —
 *  and let you type the rest.
 */
export function Pager({
  page,
  pages,
  total,
  perPage,
  onPage,
  onPerPage,
  busy,
}: {
  page: number;
  pages: number;
  total: number;
  perPage: number;
  onPage: (p: number) => void;
  onPerPage: (n: number) => void;
  busy?: boolean;
}) {
  if (total === 0) return null;

  const from = (page - 1) * perPage + 1;
  const to = Math.min(page * perPage, total);

  // where you are, its neighbours, and both ends
  const around = new Set<number>([1, pages, page]);
  for (const d of [-2, -1, 1, 2]) {
    const p = page + d;
    if (p > 1 && p < pages) around.add(p);
  }
  const shown = [...around].sort((a, b) => a - b);

  return (
    <div className="row w" style={{ gap: 6, marginTop: 14 }}>
      <span className="dim mono" style={{ fontSize: 12 }}>
        {from.toLocaleString()}–{to.toLocaleString()} of {total.toLocaleString()}
      </span>

      <div className="sp" />

      <button className="btn s" disabled={busy || page <= 1} onClick={() => onPage(1)}>
        ‹‹
      </button>
      <button className="btn s" disabled={busy || page <= 1} onClick={() => onPage(page - 1)}>
        ‹
      </button>

      {shown.map((p, i) => (
        <span key={p} className="row" style={{ gap: 6 }}>
          {i > 0 && shown[i - 1] !== p - 1 && (
            <span className="dim" style={{ fontSize: 12 }}>
              …
            </span>
          )}
          <button
            className={`btn s${p === page ? " p" : ""}`}
            disabled={busy}
            onClick={() => onPage(p)}
            style={{ minWidth: 30 }}
          >
            {p}
          </button>
        </span>
      ))}

      <button
        className="btn s"
        disabled={busy || page >= pages}
        onClick={() => onPage(page + 1)}
      >
        ›
      </button>
      <button
        className="btn s"
        disabled={busy || page >= pages}
        onClick={() => onPage(pages)}
      >
        ››
      </button>

      {pages > 5 && (
        <input
          className="f"
          type="number"
          min={1}
          max={pages}
          placeholder="go to"
          style={{ width: 74, fontSize: 12 }}
          onKeyDown={(e) => {
            if (e.key !== "Enter") return;
            const n = Number((e.target as HTMLInputElement).value);
            if (n >= 1 && n <= pages) onPage(n);
          }}
        />
      )}

      <select
        className="f"
        style={{ fontSize: 12 }}
        value={perPage}
        onChange={(e) => onPerPage(Number(e.target.value))}
      >
        {[25, 50, 100, 200].map((n) => (
          <option key={n} value={n}>
            {n} per page
          </option>
        ))}
      </select>
    </div>
  );
}
